import { useEffect, useState } from "react";
import { Star, Flag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const Stars = ({ value }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i <= value ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-200"}`} />
    ))}
  </span>
);

export const AvisView = () => {
  const { t, lang } = useLanguage();
  const [reviews, setReviews] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => api.get("/pro/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));

  useEffect(() => {
    load();
  }, []);

  const submitReply = async (id) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/reviews/${id}/reply`, { text: replyText });
      toast.success(t("Réponse publiée"));
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const report = async (id) => {
    if (!window.confirm(t("Signaler cet avis à l'admin GoSpot pour modération ?"))) return;
    try {
      await api.post(`/reviews/${id}/report`);
      toast.success(t("Avis signalé — l'admin va l'examiner"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 max-w-3xl rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6" data-testid="pro-avis-view">
      <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
        <Star className="h-4 w-4 text-amber-400" />
        Tous les avis
      </h2>
      <div className="mt-4 space-y-3">
        {reviews === null ? (
          <div className="h-24 animate-pulse rounded-2xl bg-[#F8FAFC]" />
        ) : reviews.length === 0 ? (
          <p data-testid="avis-empty" className="rounded-2xl bg-[#F8FAFC] p-6 text-center text-xs text-slate-400">
            {t("Aucun avis pour l'instant. Partage ta page pour en recevoir !")}
          </p>
        ) : (
          reviews.map((r, i) => (
            <div key={r.id} data-testid={`avis-row-${i}`} className="rounded-2xl border border-slate-100 p-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-xs font-bold text-[#2563EB]">
                  {r.name?.[0]?.toUpperCase() || "A"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-900">{r.name}</p>
                  <Stars value={r.rating} />
                </div>
                <div className="text-right">
                  <p className="flex items-center justify-end gap-2 text-[10px] text-slate-400">
                    {r.date}
                    {r.reported && (
                      <span data-testid={`avis-reported-${i}`} className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-bold text-amber-600">
                        {t("Signalé")}
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] font-medium text-slate-500">sur {r.item_label}</p>
                </div>
              </div>
              {r.comment && <p className="mt-2.5 text-xs leading-relaxed text-slate-600">{r.comment}</p>}
              {r.reply && (
                <div className="mt-2.5 rounded-xl bg-[#EFF6FF] p-3" data-testid={`avis-reply-${i}`}>
                  <p className="text-[10px] font-bold text-[#2563EB]">{t("Ta réponse")}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{r.reply.text}</p>
                </div>
              )}
              {replyFor === r.id ? (
                <div className="mt-2.5 flex gap-2">
                  <input
                    data-testid={`avis-reply-input-${i}`}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={t("Ta réponse…")}
                    className="flex-1 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs outline-none focus:border-[#2563EB]"
                  />
                  <button type="button" data-testid={`avis-reply-send-${i}`} onClick={() => submitReply(r.id)} className="rounded-full bg-[#2563EB] px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[#1D4ED8]">
                    {t("Envoyer")}
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-4">
                  <button
                    type="button"
                    data-testid={`avis-reply-open-${i}`}
                    onClick={() => {
                      setReplyFor(r.id);
                      setReplyText(r.reply?.text || "");
                    }}
                    className="text-[11px] font-semibold text-[#2563EB] hover:underline"
                  >
                    {r.reply ? "Modifier la réponse" : "Répondre"}
                  </button>
                  {!r.reported && (
                    <button
                      type="button"
                      data-testid={`avis-report-${i}`}
                      onClick={() => report(r.id)}
                      className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 transition hover:text-amber-600"
                    >
                      <Flag className="h-3 w-3" />
                      Signaler
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
